/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../../../common/constants';
import utils from '../../../../common/utils';
import { buildRubricsSearchPath } from './rubricsApiPaths';

/**
 * GET /rubrics/search — rubric name options for the evaluation report's type-ahead.
 *
 * Every response carries back the term it was asked for. A slow request for "ans" can otherwise
 * land after a later one for "answer" and overwrite the newer results, so the component compares
 * the echoed term against what is in the field before rendering anything.
 */
export default class EvalRubricNameSearchService extends BaseService {
  constructor() {
    super(constants.SEARCH_EVAL_RUBRIC_NAMES, 'EvalRubricNameSearchStatus');
  }

  async handleEvent(eventName, event) {
    const { tenantId, graphId, q, limit } = event;
    this.requestedQuery = q ?? '';
    const headers = utils.buildHeadersWithTenantId({ tenantId });
    const url = buildRubricsSearchPath({ graphId, q, limit });
    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').get(url, { headers: enhancedHeaders });
    }
    return this.getClient('aiadmin').get(url, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    const body = data?.data ?? data;
    const rubrics = Array.isArray(body?.rubrics) ? body.rubrics : [];
    Framework.getEventManager().publish(constants.SET_EVAL_RUBRIC_NAME_RESULTS, {
      rubrics,
      query: this.requestedQuery ?? ''
    });
  }

  handleError() {
    Framework.getEventManager().publish(constants.SET_EVAL_RUBRIC_NAME_RESULTS, {
      rubrics: [],
      query: this.requestedQuery ?? '',
      error: true
    });
  }
}
